import React, { Component } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import { SearchInput } from 'components/design-system/molecules/SearchInput';
import { ConfirmationModal } from 'components/v2/confirmation-modal';
import RolesPermissionsModal from 'components/modules/settings/roles-permissions-modal';
import SessionStore from 'stores/SessionStore';
import { exportUsersCsv } from 'utils/UserWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import fileDownload from 'react-file-download';
import { FilterCardHeader } from 'components/design-system/molecules/FilterCardHeader';
import { FilterBarRegistry } from 'components/design-system/molecules/FilterBarRegistry';
import {
  initialValuesFromConfigs,
  useFilterState,
} from 'components/design-system/hooks/useFilterState';
import RegisterUserForm from './RegisterUserForm';
import UserActionCreators from '../../../actions/UserActionCreators';
import UserStore from '../../../stores/UserStore';
import { makeAdminUsersFilters } from './AdminUsersList.filters';
import { AdminUsersTable } from './AdminUsersTable';
import AdminMapping from './AdminMapping';

const FILTERS = makeAdminUsersFilters();
const INITIAL_VALUES = initialValuesFromConfigs(FILTERS);

function AdminUsersListInner({ t }) {
  const [showModal, setShowModal] = React.useState(false);
  const [showRolesModal, setShowRolesModal] = React.useState(false);
  const [showActivateModal, setShowActivateModal] = React.useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = React.useState(false);
  const [selected, setSelected] = React.useState([]);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');

  // Debounce search input
  React.useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { values, displayed, labels, setValue, setMany, setLabel, toggle } =
    useFilterState(INITIAL_VALUES);

  const onChangeFilter = React.useCallback(
    (id, value) => setValue(id, value),
    [setValue],
  );
  const onToggleFilter = React.useCallback((id) => toggle(id), [toggle]);
  const onReset = React.useCallback(() => {
    const cleared = {};
    for (const cfg of FILTERS) {
      cleared[cfg.id] =
        cfg.kind === 'select-multi' || cfg.kind === 'autocomplete-multi'
          ? []
          : cfg.kind === 'toggle'
            ? false
            : cfg.kind === 'text'
              ? ''
              : null;
    }
    setMany(cleared);
    setSearchInput('');
    setSearch('');
  }, [setMany]);

  const getSelectedUsers = () => {
    const all = (UserStore.getAdminUsers() || []).flatMap(
      (page) => page?.users || [],
    );
    return all
      .filter((u) => selected.includes(u._id))
      .map((u) => ({
        ...u,
        name: `${(u.first_name || '').trim()} ${(u.last_name || '').trim()}`.trim(),
      }));
  };

  const canActivate = () => {
    const users = getSelectedUsers();
    return users.length > 0 && users.every((u) => !u.active);
  };
  const canDeactivate = () => {
    const users = getSelectedUsers();
    return users.length > 0 && users.every((u) => !!u.active);
  };
  const canChangeRoles = () => selected.length > 0;
  const canRegisterNewUser = () => SessionStore.isAdmin();
  const canPerformActionOnUsers = () => SessionStore.isAdmin();

  const handleActivate = async () => {
    setShowActivateModal(false);
    await UserActionCreators.activateUsers(selected, {});
    setRefreshKey((k) => k + 1);
  };
  const handleDeactivate = async () => {
    setShowDeactivateModal(false);
    await UserActionCreators.deactivateUsers(selected, {});
    setRefreshKey((k) => k + 1);
  };
  const handleRolesSave = async (users) => {
    setShowRolesModal(false);
    await UserActionCreators.updateUsersRoles(users);
    setRefreshKey((k) => k + 1);
  };

  const exportCsv = async () => {
    try {
      setExportLoading(true);
      const exportFilters = search ? { ...values, search } : values;
      const { entity } = await exportUsersCsv(
        AdminMapping.columns,
        exportFilters,
      );
      fileDownload(entity, 'registered_users.csv');
    } catch {
      showToast(t('EXPORT_CSV'), t('CSV_EXPORT_REQUEST_FAILED'), 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const confirmActivateMessage = () => {
    const users = getSelectedUsers();
    return t('CONFIRM_USER_ACTIVATION', {
      names: users.map((u) => u.name).join(', '),
    });
  };
  const confirmDeactivateMessage = () => {
    const users = getSelectedUsers();
    return t('CONFIRM_USER_DEACTIVATION', {
      names: users.map((u) => u.name).join(', '),
    });
  };

  return (
    <Card>
      <FilterCardHeader
        title="Admin Users"
        onReset={onReset}
        actions={
          <div className="flex items-center gap-2">
            {canPerformActionOnUsers() && (
              <Button
                variant="outline"
                size="sm"
                disabled={exportLoading}
                onClick={exportCsv}
              >
                {exportLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {t('EXPORT')}
              </Button>
            )}
            {canPerformActionOnUsers() && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {titleize(t('ACTIONS'))}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={!canActivate()}
                    onClick={() => setShowActivateModal(true)}
                  >
                    {titleize(t('ACTIVE'))}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!canDeactivate()}
                    onClick={() => setShowDeactivateModal(true)}
                  >
                    {titleize(t('DEACTIVATED'))}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!canChangeRoles()}
                    onClick={() => setShowRolesModal(true)}
                  >
                    {t('ROLES_AND_PERMISSIONS')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button
              size="sm"
              disabled={!canRegisterNewUser()}
              onClick={() => setShowModal(true)}
            >
              Register New User
            </Button>
          </div>
        }
      >
        <FilterBarRegistry
          configs={FILTERS}
          values={values}
          labels={labels}
          displayed={displayed}
          onChange={onChangeFilter}
          onLabelChange={(id, optionId, label) => setLabel(id, optionId, label)}
          onToggle={onToggleFilter}
        />
      </FilterCardHeader>
      <CardContent className="space-y-4">
        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search by name or email"
          className="max-w-md"
        />
        <AdminUsersTable
          status={values.status}
          search={search}
          selectedIds={selected}
          onSelectedChange={setSelected}
          refreshKey={refreshKey}
        />
      </CardContent>

      <RegisterUserForm show={showModal} onHide={() => setShowModal(false)} />
      <ConfirmationModal
        title={t('ARE_YOU_SURE')}
        description={confirmDeactivateMessage()}
        confirmText={t('OK')}
        onConfirm={handleDeactivate}
        open={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
      />
      <ConfirmationModal
        title={t('ARE_YOU_SURE')}
        description={confirmActivateMessage()}
        confirmText={t('OK')}
        onConfirm={handleActivate}
        open={showActivateModal}
        onHide={() => setShowActivateModal(false)}
      />
      {showRolesModal && (
        <RolesPermissionsModal
          show={showRolesModal}
          onHide={() => setShowRolesModal(false)}
          userIds={selected}
          onSave={handleRolesSave}
        />
      )}
    </Card>
  );
}

class AdminUsersList extends Component {
  render() {
    return <AdminUsersListInner t={this.props.t} />;
  }
}

export default withTranslation()(AdminUsersList);
